import { customInstance } from '@/features/auth/axios-config'
import {
  GetApprovedFilesParams,
  GetAvgCycleTimeParams,
  GetAvgDaysPerFileTrendParams,
  GetCommentDensityParams,
  GetCompletionRateParams,
  GetDsCompletionRateParams,
  GetLateDeliverablesParams,
  GetNoOfProjectsParams,
  GetOpenCommentsParams,
  GetResolutionRateParams,
  GetResolutionRateTrendParams,
  GetReviewCycleTimeParams,
  GetReviewTimeTrendParams,
  GetTflCompletionRateParams,
  GetTimeToFirstCommentParams,
  ResponseApprovedFiles,
  ResponseAvgCycleTime,
  ResponseAvgDaysPerFileTrend,
  ResponseCommentDensity,
  ResponseCompletionRate,
  ResponseDsCompletionRate,
  ResponseLateDeliverables,
  ResponseMostActiveUsers,
  ResponseNoOfProjects,
  ResponseOpenComments,
  ResponseResolutionRate,
  ResponseResolutionRateTrend,
  ResponseReviewCycleTime,
  ResponseReviewTimeTrend,
  ResponseTflCompletionRate,
  ResponseTimeToFirstComment,
  SuccessResponseList,
} from '@/schema'

const API_ENDPOINT_ANALYTICS_METRICS = '/document/v1/analytics/metrics'
const API_ENDPOINT_ANALYTICS_TRENDS = '/document/v1/analytics/trends'
const API_ENDPOINT_AUDIT = '/admin/v1/audits/reports'

export type SecondParameter<T extends (...args: never) => unknown> =
  Parameters<T>[1]

/**
 * Get the top 5 most active users ranked by total audit activity.
 * 
 * Return the top 5 most active users ranked by total audit activity. 
 * Optionally scoped to a single company via company_id. 
 * Activity includes document views, comments created, replies created, 
 * and document status changes (approved/rejected/revisioned/updated).

**Filters:**
- company_id: Optional. Filter by specific company

**Response:**
- metric: Total count of users returned (always 5 or less)
- data: List of top 5 most active users sorted by total activity

**Note:** Returns maximum 5 users with highest total activity.
 * @summary Get top 5 most active users by audit activity
 */
export const getMostActiveUsers = (
  params?: { company_id?: string },
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseMostActiveUsers>>(
    {
      url: `${API_ENDPOINT_AUDIT}/most-active-users`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get drill-down data for projects with active and completed deliverable counts.

**Filters:**
- company_id: Optional filter by specific company

**Response:**
- metric: Total number of active projects (row count)
- data: List of ALL projects with deliverable counts (sorted by active_deliverables DESC)

**Note:** Returns complete dataset without pagination. Default sort: active_deliverables DESC.
 * @summary Get project deliverable statistics
 */
export const getNoOfProjects = async (
  params?: GetNoOfProjectsParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseNoOfProjects>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/active-projects`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get drill-down data for late deliverables with overdue days calculation.

**Filters:**
- level: Required. Aggregation level ("company" or "project")
- company_id: Optional for COMPANY level
- project_id: Required for PROJECT level

**Response:**
- metric: Total number of late deliverables (row count)
- data: List of ALL late deliverables with overdue days (sorted by overdue_days DESC - most overdue first)

**Note:** Only includes deliverables with ds_due_date < current_date. Returns complete dataset without pagination.
 * @summary Get late deliverables with overdue information
 */
export const getLateDeliverables = (
  params: GetLateDeliverablesParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseLateDeliverables>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/late-deliverable-sets`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get drill-down data for comment density metrics at specified organizational level.

**Filters:**
- level: Required. Aggregation level ("company", "project", or "deliverable_set")
- company_id: Optional for COMPANY level
- project_id: Required for PROJECT level
- deliverable_set_id: Required for DELIVERABLE_SET level

**Response:**
- metric: Average comment density across ALL items at specified level
- data: List of ALL items with individual comment density metrics (sorted by comment_density DESC - highest density first)

**Note:** Comment density = (total_comments / file_count). Returns complete dataset without pagination.
 * @summary Get comment density by project
 */
export const getCommentDensity = (
  params: GetCommentDensityParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseCommentDensity>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/comment-density`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get drill-down data for completion rate metrics.

**Filters (applied in hierarchical order):**
- project_id: Filter by specific project
- deliverable_set_id: Filter by specific deliverable set

**Response:**
- metric: Overall completion rate percentage (approved / total * 100)
- data: List with completion rate breakdown (approved_count, total_count)

**Note:** Completion rate = (approved_count / total_count) * 100. Only includes active projects.
 * @summary Get completion rate metrics
 */
export const getCompletionRate = (
  params?: GetCompletionRateParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseCompletionRate>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/completion-rate`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get drill-down data for resolution rate metrics.

**Filters:**
- level: Required. Aggregation level ("project" or "deliverable_set")
- project_id: Required for PROJECT level
- deliverable_set_id: Required for DELIVERABLE_SET level

**Response:**
- metric: Overall resolution rate percentage (resolved / total * 100)
- data: List with resolution rate breakdown (resolved_count, open_count)

**Note:** Resolution rate = (resolved_count / (resolved_count + open_count)) * 100.
 * @summary Get resolution rate metrics
 */
export const getResolutionRate = (
  params: GetResolutionRateParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseResolutionRate>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/resolution-rate`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get comprehensive comment resolution metrics at specified level.
 * 
 * This endpoint serves as the single source of truth for ALL comment status metrics. 
 * Use this endpoint to retrieve open comments count, resolved comments count, and resolution rate.

**Filters:**
- level: Required. Aggregation level ("project" or "deliverable_set")
- project_id: Required for PROJECT level
- deliverable_set_id: Required for DELIVERABLE_SET level

**Response:**
- metric: Overall resolution rate percentage (resolved / total * 100)
- data: List with resolution metrics breakdown (resolved_count, open_count)

**Client Usage:**
- Need resolution rate? → Use metric field (percentage)
- Need open comments count? → Use data[0].open_count
- Need resolved comments count? → Use data[0].resolved_count

**Note:** Resolution rate = (resolved_count / (resolved_count + open_count)) * 100.
 * @summary Get comprehensive comment resolution metrics
 */
export const getResolutionMetrics = (
  params: GetResolutionRateParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseResolutionRate>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/resolution-metrics`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get drill-down data for open comments count.

**Filters:**
- level: Required. Aggregation level ("project" or "deliverable_set")
- project_id: Required for PROJECT level
- deliverable_set_id: Required for DELIVERABLE_SET level

**Response:**
- metric: Total count of open comments (threads)
- data: List with open comments count breakdown

**Note:** Counts threads with status = 'open' at the specified aggregation level.
 * @summary Get open comments count metrics
 */
export const getOpenComments = (
  params: GetOpenCommentsParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseOpenComments>>(
    {
      url: `/v1/analytics/metrics/open-comments`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get time-series trend data for resolution rates with configurable time bucketing.

**Filters:**
- bucket_type: Required. Aggregation period ("week" or "month").
- time_range: Required. Predefined time range.

**Allowed Combinations:**

**TIME_RANGE → Allowed BUCKET_TYPES**
- this_month       → ["week"]
- last_month       → ["week"]
- last_quarter     → ["week"]
- this_quarter     → ["month"]
- last_quarter     → ["month"]
- last_6_months    → ["month"]
- year_to_date     → ["month"]

**Response:**
- metric: Overall average resolution rate (%) across all time buckets
- data: Time-series data points sorted chronologically by bucket_date (earliest first)

**Business Logic:**
Resolution rate calculated as:
1. Daily: (Resolved Comments / Total Comments per File) × 100
2. Bucket: Average of daily rates within the bucket period
3. Overall Metric: Average across all buckets in the time range

**Note:**
- Invalid bucket_type/time_range combinations return HTTP 422 validation error
- Empty data returns metric=0.0 with empty data array
- All dates are inclusive ranges based on current system date
 * @summary Get resolution rate trend over time
 */
export const getResolutionRateTrend = (
  params?: GetResolutionRateTrendParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseResolutionRateTrend>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_TRENDS}/resolution-rate`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get time-series trend data for review cycle times with configurable time bucketing.

**Filters:**
- bucket_type: Required. Aggregation period ("week" or "month").
- time_range: Required. Predefined time range.

**Allowed Combinations:**

**TIME_RANGE → Allowed BUCKET_TYPES**
- this_month       → ["week"]
- last_month       → ["week"]
- last_quarter     → ["week"]
- this_quarter     → ["month"]
- last_quarter     → ["month"]
- last_6_months    → ["month"]
- year_to_date     → ["month"]

**Response:**
- metric: Overall average review cycle time (days) across all time buckets
- data: Time-series data points sorted chronologically by bucket_date (earliest first)

**Business Logic:**
Review cycle time calculated as:
1. Per deliverable set: (ds_approved_at - ds_created_at) in days
2. Bucket: Average cycle times within the bucket period
3. Overall Metric: Average across all buckets in the time range

**Note:**
- Only includes deliverable sets with status = "approved"
- Filters by ds_approved_at (not ds_created_at)
- Invalid bucket_type/time_range combinations return HTTP 422 validation error
- Empty data returns metric=0.0 with empty data array
- All dates are inclusive ranges based on current system date
 * @summary Get review time trend over time
 */
export const getReviewTimeTrend = (
  params?: GetReviewTimeTrendParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseReviewTimeTrend>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_TRENDS}/review-time`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get weighted average cycle time per file over time with week or month aggregation.
 * 
 * This is a TREND endpoint showing how average days per file changes over time:
 * - Overall weighted average across entire period (metric field)
 * - Time-series breakdown by week/month buckets (data field)

**Filters:**
- bucket_type: Required. Aggregation period ("week" or "month"). Default: week
- time_range: Required. Predefined time range. Default: this_month
- company_id: Optional. Filter by specific company. If not provided, returns data across all companies.

**Allowed Combinations:**

**TIME_RANGE → Allowed BUCKET_TYPES**
- this_month       → ["week"]
- last_month       → ["week"]
- this_quarter     → ["month"]
- last_quarter     → ["month"]
- last_6_months    → ["month"]
- year_to_date     → ["month"]

**Response:**
- metric: Overall weighted average days per file across entire time period (float)
- data: Time-series data points sorted chronologically by bucket_date (earliest first)

**Note:**
- Invalid bucket_type/time_range combinations return HTTP 422 validation error
- Empty data returns metric=0 with empty data array
- All dates are inclusive ranges based on current system date
 * @summary Get weighted average days per file trend over time
 */
export const getAvgDaysPerFileTrend = (
  params?: GetAvgDaysPerFileTrendParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseAvgDaysPerFileTrend>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_TRENDS}/avg-days-per-file`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get average cycle time across all approved deliverable sets in a project.
 * 
 * This metric calculates the average time (in days) from deliverable set creation to approval. 
 * It only operates at the project level.

**Filters:**
- project_id: Required - Project ID to get average cycle time for

**Response:**
- metric: Overall average cycle time in days across all approved deliverable sets (0 if none)
- data: List of ALL approved deliverable sets with individual cycle times
  - deliverable_set_id: Unique deliverable set identifier
  - ds_name: Deliverable set name
  - cycle_time_days: Cycle time in days (ceiling rounded)

**Business Logic:**
- Cycle time = CEIL((ds_approved_at - ds_created_at) / 86400)
- Only includes deliverable sets with status='approved'
- Returns drill-down data showing each deliverable set's cycle time
- Overall average is calculated across all approved deliverable sets
 * @summary Get average cycle time for a project
 */
export const getAvgCycleTime = (
  params: GetAvgCycleTimeParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseAvgCycleTime>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/avg-cycle-time`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get review cycle time at deliverable set level.
 * 
 * This metric calculates the time (in days) from deliverable set creation to approval. 
 * It only operates at the deliverable set level.

**Filters:**
- deliverable_set_id: Required - Deliverable set ID to get cycle time for

**Response:**
- metric: Review cycle time in days (0 if not approved)
- data: List with cycle time breakdown

**Business Logic:**
- Cycle time = CEIL((approved_at - created_at) / 86400)
- Returns 0 if deliverable set is not approved
- Only includes deliverable sets with status='approved'

**Note:** This is a deliverable-set-scoped metric. You must provide a deliverable_set_id.
 * @summary Get review cycle time for a specific deliverable set
 */
export const getReviewCycleTime = (
  params: GetReviewCycleTimeParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseReviewCycleTime>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/review-cycle-time`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get TFL (file) completion rate at deliverable set level.
 * 
 * This metric calculates the completion rate of files within a deliverable set. 
 * It only operates at the deliverable set level (not at project level).

**Filters:**
- deliverable_set_id: Required - Deliverable set ID to get completion rate for

**Response:**
- metric: Overall completion rate percentage (approved_files / total_files * 100)
- data: List with completion rate breakdown (approved_files, total_files)

**Business Logic:**
- Completion rate = (approved_files / total_files) * 100
- Returns the count of approved files and total files in the deliverable set
- metric field is 0 if total_files is 0

**Note:** This is a deliverable-set-scoped metric. You must provide a deliverable_set_id.
 * @summary Get TFL completion rate for a specific deliverable set
 */
export const getTflCompletionRate = (
  params: GetTflCompletionRateParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseTflCompletionRate>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/tfl-completion-rate`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get deliverable set completion rate at project level.
 * 
 * This metric calculates the completion rate of deliverable sets within a project. 
 * It only operates at the project level (not at individual deliverable set level).

**Filters:**
- project_id: Required - Project ID to get completion rate for

**Response:**
- metric: Overall completion rate percentage (approved_count / total_count * 100)
- data: List with completion rate breakdown (approved_count, total_count)

**Business Logic:**
- Completion rate = (approved_count / total_count) * 100
- Returns the count of approved deliverable sets and total deliverable sets in the project
- metric field is 0 if total_count is 0

**Note:** This is a project-scoped metric. You must provide a project_id.
 * @summary Get deliverable set completion rate for a specific project
 */
export const getDsCompletionRate = (
  params: GetDsCompletionRateParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseDsCompletionRate>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/ds-completion-rate`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get total count of approved files within specified time period with bucket drill-down.
 * 
 * This is a METRIC endpoint (not trend) showing:
 * - Total approved files count across entire period (metric field)
 * - Drill-down breakdown by week/month buckets (data field)

**Filters:**
- bucket_type: Required. Aggregation period ("week" or "month"). Default: week
- time_range: Required. Predefined time range. Default: this_month
- company_id: Optional. Filter by specific company. If not provided, returns data across all companies.

**Allowed Combinations:**

**TIME_RANGE → Allowed BUCKET_TYPES**
- this_month       → ["week"]
- last_month       → ["week"]
- this_quarter     → ["month"]
- last_quarter     → ["month"]
- last_6_months    → ["month"]
- year_to_date     → ["month"]

**Response:**
- metric: Total count of approved files across entire time period (int)
- data: Bucket-level breakdown sorted chronologically by bucket_date (earliest first)

**Business Logic:**
- Counts files with status='approved' within the specified time range
- Aggregates by bucket_type (week or month)
- metric is the sum of all approved_files_count across all buckets

**Note:**
- Invalid bucket_type/time_range combinations return HTTP 422 validation error
- Empty data returns metric=0 with empty data array
- All dates are inclusive ranges based on current system date
 * @summary Get approved files count with bucket drill-down
 */
export const getApprovedFiles = (
  params?: GetApprovedFilesParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseApprovedFiles>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/approved-files`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}

/**
 * Get time to first comment at deliverable set level.
 * 
 * This metric calculates the time (in days) from the first document upload to the first comment creation 
 * within a deliverable set. It only operates at the deliverable set level.

**Filters:**
- deliverable_set_id: Required - Deliverable set ID to get metric for

**Response:**
- metric: Time to first comment in days (0 if no comments exist)
- data: List with time to first comment breakdown

**Business Logic:**
- Time to first comment = First comment created_at - First document upload timestamp
- Returns 0 if no comments exist in the deliverable set
- Calculated in days

**Note:** This is a deliverable-set-scoped metric. You must provide a deliverable_set_id.
 * @summary Get time to first comment for a specific deliverable set
 */
export const getTimeToFirstComment = (
  params: GetTimeToFirstCommentParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseList<ResponseTimeToFirstComment>>(
    {
      url: `${API_ENDPOINT_ANALYTICS_METRICS}/time-to-first-comment`,
      method: 'GET',
      params,
      signal,
    },
    options
  )
}
